import dayjs from 'dayjs';
import { KeyboardEvent, SyntheticEvent, useCallback } from 'react';
import { Control, FieldArrayWithId, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { NumberInput } from 'core/components';
import { DB_DATE_FORMAT } from 'core/constants';
import { useFormContext } from 'core/states';
import { isEnterKeyPress, isEscapeKeyPress } from 'core/utils';
import { IUpsertLogsFormData } from 'jobs/components/JobDetails';
import { Size } from 'theme/enums';

import { ITimeLogCalendarDay } from './types';

interface IProps {
  day: ITimeLogCalendarDay;
  value: number | null;
  setValue: (value: number | null) => void;
  onCancelEdit: () => void;
}

export const HoursInput = ({ day, value, setValue, onCancelEdit }: IProps) => {
  const { t } = useTranslation();

  const { control } = useFormContext();

  const { fields, append, update, remove } = useFieldArray<IUpsertLogsFormData, 'timeLogs', 'id'>({
    control: control as unknown as Control<IUpsertLogsFormData>,
    name: 'timeLogs',
  });

  const onSaveHours = useCallback(
    (dateKey: string) => {
      if (!!value && value > 0) {
        const existingTimeLogIndex = fields.findIndex(
          (field: FieldArrayWithId<IUpsertLogsFormData, 'timeLogs'>) =>
            dayjs(field.activityDate).format(DB_DATE_FORMAT) === dateKey
        );

        if (existingTimeLogIndex >= 0) {
          update(existingTimeLogIndex, {
            ...fields[existingTimeLogIndex],
            duration: value,
          });
        } else {
          append({
            activityDate: dateKey,
            duration: value,
          });
        }
      } else if (!value) {
        const timeLogsToRemove = fields
          .map((field: FieldArrayWithId<IUpsertLogsFormData, 'timeLogs'>, index: number) =>
            dayjs(field.activityDate).format(DB_DATE_FORMAT) === dateKey ? index : null
          )
          .filter((index) => index !== null)
          .reverse();

        timeLogsToRemove.forEach((index: number) => remove(index));
      }

      onCancelEdit();
    },
    [value, onCancelEdit, fields, update, append, remove]
  );

  const onChangeHours = useCallback(
    (e: {
      target: { name: string; value: string };
      source: 'prop' | 'event';
      event?: SyntheticEvent;
    }) => {
      setValue(e.target.value ? Number(e.target.value) : null);
    },
    [setValue]
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>, dateKey: string) => {
      if (isEnterKeyPress(e)) {
        onSaveHours(dateKey);

        return;
      }

      if (isEscapeKeyPress(e)) {
        onCancelEdit();
      }
    },
    [onSaveHours, onCancelEdit]
  );

  return (
    <NumberInput
      name="duration"
      size={Size.SM}
      value={value}
      onChange={(e) => onChangeHours(e)}
      onKeyDown={(e) => onKeyDown(e, day.dateKey)}
      onBlur={() => onSaveHours(day.dateKey)}
      placeholder={` ${t('labelHrs')}`}
      slotProps={{
        input: {
          min: 0,
          max: 24,
          thousandSeparator: true,
          decimalScale: 2,
          suffix: ` ${t('labelHrs')}`,
        },
      }}
      sx={{ '--Input-minHeight': '28px' }}
      autoFocus
    />
  );
};
